import React, { FC, useContext, useLayoutEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArchivalAidMetadata, ArchivalUnitCopy, Result } from 'types';

import { Theme, createStyles, makeStyles, useTheme } from '@material-ui/core';

import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { useVariantContext } from 'components/grid/v2/context/variant-context';
import { levelIcon } from 'components/grid/grid-hooks/breadcrumbs-hook';
import { useQuery } from 'react-query';
import fetch from 'utils/fetch';

import { HierarchyContext } from '../context/hierarchy-context';
import useMeasure from 'react-use-measure';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    active: {
      backgroundColor: theme.palette.primary.dark,
      color: 'white!important',
      padding: '0px 4px',
    },
    link: {
      color: 'white',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    icon: {
      marginRight: 4,
      fontSize: 11,
    },

    listItem: {
      fontSize: 13,
      lineHeight: '2em',
      fontWeight: 500,
      color: theme.palette.grey[300],
    },
    listItemActive: {
      fontSize: 13,
      lineHeight: '2em',
      fontWeight: 500,
      color: theme.palette.primary.light,
    },
    itemWrapper: {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    itemText: {
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      paddingRight: 12,
    },
  })
);

export const useAidChildren = (id?: string, enabled?: boolean) =>
  useQuery(
    ['aid-children', id],
    () =>
      fetch('/api/archival-unit/public/list', {
        method: 'POST',
        headers: new Headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          sort: [{ field: 'numericId', order: 'ASC' }],
          filters: [
            // TODO: zase test na invalidated?
            {
              field: 'describingArchivalAids.id',
              operation: 'EQ',
              value: id,
            },
          ],
          size: -1,
        }),
      }).then(async (r) => (await r.json()) as Result<ArchivalUnitCopy>),
    { enabled: !!id && enabled }
  );

type Props = {
  aid: ArchivalAidMetadata;
  level: number;
  itemIndex?: number;
  setHeight?: (height: number) => void;
  height?: number;
  activeRowIndex?: number;
};

const AidListItem: FC<Props> = ({
  aid,
  level = 1,
  setHeight,
  height,
  activeRowIndex,
  itemIndex,
}) => {
  const variant = useVariantContext();

  const { archivalAidId, archivalUnitId, rootUrl, getRootId } = useContext(
    HierarchyContext
  );
  //const urlAidId = variant === 'aids' ? id : archivalAidId;

  const ref = useRef<HTMLDivElement>(null);
  const isActive = aid.id === archivalAidId;

  const childrenResponse = useAidChildren(aid.id, isActive && aid.hasChildren);

  const [wrapRef, { height: wHeight }] = useMeasure();

  const searchParams = new URLSearchParams(window.location.search);
  const originatorId = searchParams.get('originator') ?? undefined;

  const originatorParam = `${
    originatorId ? `?originator=${originatorId}` : ''
  }`;

  const createLink = (archivalUnitId?: string): string => {
    const base = `${rootUrl}/${getRootId?.()}`;
    if (variant === 'funds') {
      if (aid.hasEadNode) {
        return `${base}/ead/${aid.eadNodeId}${originatorParam}`;
      }
      if (archivalUnitId) {
        return `${base}/archivalie/${archivalUnitId}${originatorParam}`;
      }

      return `${base}/pomucka/${aid.id}${originatorParam}`;
    }
    if (archivalUnitId) {
      return `${base}/archivalie/${archivalUnitId}${originatorParam}`;
    }
    return `${rootUrl}/${aid.id}${
      aid?.hasEadNode ? `/ead/${aid.eadNodeId}` : ''
    }${originatorParam}`;
  };

  useLayoutEffect(() => {
    if (isActive) {
      const newHeight = ref.current?.getBoundingClientRect?.()?.height ?? 25;
      if (height !== newHeight || activeRowIndex !== itemIndex) {
        setHeight?.(newHeight + wHeight);
      }
    }
  }, [ref, isActive, height, setHeight, activeRowIndex, itemIndex, wHeight]);

  const classes = useStyles();
  const theme = useTheme();
  const Icon = levelIcon['aid'];

  const listItemClass =
    isActive && aid?.id === archivalAidId
      ? classes.listItemActive
      : classes.listItem;

  return (
    <div ref={ref} style={{ paddingLeft: 10 * level, paddingRight: 16 }}>
      <Link className={`${classes.link} ${listItemClass}`} to={createLink()}>
        <div className={classes.itemWrapper}>
          <div className={classes.itemText}>
            <span>
              <Icon className={classes.icon} />
            </span>
            {aid?.name}
          </div>
          {aid?.hasChildren ? (
            isActive ? (
              <ExpandLessIcon classes={{ root: listItemClass }} />
            ) : (
              <ExpandMoreIcon classes={{ root: listItemClass }} />
            )
          ) : null}
        </div>
      </Link>

      {isActive && !aid.hasEadNode && childrenResponse.data && (
        <div style={{ color: 'white', paddingLeft: 16 }} ref={wrapRef}>
          {(childrenResponse.data.items ?? []).map((d) => (
            <Link
              key={d.id}
              className={`${classes.link} `}
              to={createLink(d.id)}
            >
              <div
                className={classes.itemWrapper}
                style={{
                  backgroundColor:
                    archivalUnitId === d.id ? theme.palette.primary.dark : '',
                }}
              >
                <div className={classes.itemText}>
                  <span>
                    <Icon className={classes.icon} />
                  </span>
                  {d.label}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default AidListItem;
